import React, {Fragment, useCallback, useMemo, useState} from 'react';
import CustomerCell from "../common/table/customer-cell";
import {getCustomFormat, getFormattedDate} from "../../utils/timeUtils";
import {getWebPathParam} from "../../utils/converter";
import moment from "moment";
import {
    C_CONTAINER_GENERAL_INFORMATION,
    CONTAINER,
    CONTAINERS,
    DOCUMENTS,
    EVENTS,
    LOGISTICS,
    SHIPPING,
    SHIPPING_DETAIL
} from "../../routes/endpoints";
import ShippingIcon from "../../resources/icons/ShippingIcon";
import TruckIcon from "../../resources/icons/TruckIcon";
import AirIcon from "../../resources/icons/AirIcon";
import { Dialog, Transition } from "@headlessui/react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {hasPermission} from "../partials/restricted/permission-wrapper";
import {pg, pt} from "../../constants/permissions";
import {SEA, LAND, AIR} from "../../constants/config";
import {ChevronDownIcon, ClockIcon, CubeIcon} from "@heroicons/react/outline";
import XIcon from "@heroicons/react/solid/XIcon";

function UnconfirmedShipping({data, height}) {

  const { t } = useTranslation();
  const shippings = useMemo(() => data?.shippings ?? [], [data?.shippings]);
  const totalRows = data?.total_rows ?? shippings.length;
  const hasRows = totalRows > 0;
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const handlePreviewOpen = useCallback((url) => {
    if (!url) {
      return;
    }
    setPreviewUrl(resolveFrameUrl(url));
    setIsPreviewOpen(true);
  }, []);

  const handlePreviewClose = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewUrl(null);
  }, []);

  const groupedShippings = useMemo(() => {
    if (!hasRows) {
      return [];
    }

    const buckets = shippings.reduce((acc, shipping) => {
      const departureMoment = moment(shipping.departure_date, "YYYY-M-D HH:mm:ss");
      const startOfWeek = departureMoment.clone().startOf("week");
      const endOfWeek = departureMoment.clone().endOf("week");
      const bucketKey = startOfWeek.format("YYYY-MM-DD");

      if (!acc[bucketKey]) {
        acc[bucketKey] = {
          key: bucketKey,
          label: `${startOfWeek.format("D MMMM YYYY")} - ${endOfWeek.format("D MMMM YYYY")}`,
          timestamp: startOfWeek.valueOf(),
          shippings: []
        };
      }

      acc[bucketKey].shippings.push(shipping);
      return acc;
    }, {});

    return Object.values(buckets).sort((a, b) => b.timestamp - a.timestamp);
  }, [hasRows, shippings]);

  const totalLoads = useMemo(() => {
    if (!hasRows) {
      return 0;
    }

    return shippings.reduce((acc, shipping) => {
      return acc + (shipping?.containers?.length ?? 0);
    }, 0);
  }, [hasRows, shippings]);

  const wrapperStyle = height ? { height: `${height}px` } : undefined;

  if (!hasRows) {
    const emptyStyle = height ? { minHeight: `${height}px` } : undefined;
    return (
      <div style={emptyStyle} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white/60 px-6 py-10 text-center">
        <ShippingIcon
          className="h-24 w-24 text-indigo-600"
          aria-hidden="true"
        />
        <p className="mt-4 text-2xl font-extrabold text-gray-900 sm:text-2xl sm:tracking-tight lg:text-2xl">
          {t("dashboard.unconfirmed_shipping.not_found_title")}
        </p>
        <p className="mx-auto mt-2 max-w-xl text-md text-gray-500">
          {t("dashboard.unconfirmed_shipping.not_found_description")}
        </p>
      </div>
    );
  }

  return (
    <div style={wrapperStyle} className="flex h-full flex-col ">
      <div className="grid  sm:grid-cols-2">
        <SummaryCard
          variant="primary"
          label={t("dashboard.unconfirmed_shipping.summary_shipments")}
          helper={t("dashboard.unconfirmed_shipping.title")}
          value={numberFormatter.format(totalRows)}
          icon={<ShippingIcon className="h-14 w-14 text-white/80" />}
        />
        <SummaryCard
          label={t("dashboard.unconfirmed_shipping.summary_loads")}
          helper={`${t("app.container.container")} / ${t("app.container.truck")} / ${t("app.container.uld")}`}
          value={numberFormatter.format(totalLoads)}
          icon={<CubeIcon className="h-10 w-10 text-indigo-600" />}
        />
      </div>
      <nav className="flex-1 overflow-y-auto" aria-label="Directory">
        {groupedShippings.map((group) => (
          <section key={group.key} className="relative  bg-white/95  ">
            <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-gray-50 to-gray-100 px-5 py-3 ">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t("dashboard.unconfirmed_shipping.title")}
                </p>
                <h3 className="text-base font-semibold text-gray-900">
                  {group.label.charAt(0).toUpperCase() + group.label.slice(1)}
                </h3>
              </div>
              <p className="text-xs font-semibold text-indigo-600">
                {t("dashboard.unconfirmed_shipping.week_summary", { count: group.shippings.length })}
              </p>
            </div>
            <ul className="list-none space-y-3   ">
              {group.shippings.map((item) => (
                <li key={item.id}>
                  <Row item={item} onPreview={handlePreviewOpen} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
      <FullscreenFrame isOpen={isPreviewOpen} onClose={handlePreviewClose} url={previewUrl} />
    </div>
  );
}

const Row = ({ item, onPreview = () => {} }) => {
    const { permissions } = useSelector((s) => s.userCompany);
    const { companyType } = useSelector((s) => s.user);
    const [isActive, setActive] = useState(false);
    const { t } = useTranslation();
    const shippingTypeMeta = useMemo(() => getShippingTypeMeta(item?.shipping_type?.code, t), [item?.shipping_type?.code, t]);
    const defaultLoadsLabel = (item.shipping_type && (item.shipping_type.code === SEA))
        ? t("dashboard.unconfirmed_shipping.flag_containers")
        : t("dashboard.unconfirmed_shipping.flag_trucks");
    const loadsLabel = shippingTypeMeta?.containerLabel ?? defaultLoadsLabel;
    const containersCount = item?.containers?.length ?? 0;
    const collapsibleId = `unconfirmed-shipping-${item.id}`;
    const shippingTypeLabel = shippingTypeMeta?.name ?? getShippingTypeLabel(item.shipping_type);
    const TypeIcon = shippingTypeMeta?.icon ?? null;

    let url = '#0';

    const isLinked = hasPermission(
        permissions,
        pg.SHIPPING,
        pt.EDIT,
        item.customer.id,
        companyType,
        true
    );

    const isLinkedContainer = hasPermission(
        permissions,
        pg.CONTAINER,
        pt.EDIT,
        item.customer.id,
        companyType,
        true
    );
    const isLinkedDocument = hasPermission(
        permissions,
        pg.DOCUMENT,
        pt.EDIT,
        item.customer.id,
        companyType,
        true
    );
    const isLinkedEvent = hasPermission(
        permissions,
        pg.EVENT,
        pt.EDIT,
        item.customer.id,
        companyType,
        true
    );



    if(isLinked){
        url = getWebPathParam([LOGISTICS, SHIPPING, item.id, SHIPPING_DETAIL]);
    }else{
        if(isLinkedContainer){
            url = getWebPathParam([LOGISTICS, SHIPPING, item.id, CONTAINERS]);
        }else{
            if(isLinkedDocument){
                url = getWebPathParam([LOGISTICS, SHIPPING, item.id, DOCUMENTS]);
            }else{
                if(isLinkedEvent){
                    url = getWebPathParam([LOGISTICS, SHIPPING, item.id, EVENTS]);
                }
            }
        }
    }


    return (
        <article className="bg-white/90 p-4 text-sm text-gray-700 border-b  ">
            <div className="flex flex-col ho  sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                    <a
                        href={url}
                        className="block"
                        onClick={(event) => {
                            event.preventDefault();
                            onPreview(url);
                        }}
                    >
                        <CustomerCell customer={item.customer} extra={item?.description ?? false} />
                    </a>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold">
                        {
                            shippingTypeLabel && (
                                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg ${getShippingBadgeClasses(item.shipping_type.code)}`}>
                                    {
                                        TypeIcon && (
                                            <TypeIcon className="h-4 w-4" aria-hidden="true" />
                                        )
                                    }
                                    {shippingTypeLabel}
                                </span>
                            )
                        }
                        {
                            containersCount > 0 && (
                                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-gray-100 text-gray-900">
                                    {containersCount} {loadsLabel}
                                </span>
                            )
                        }
                    </div>
                </div>
                <div className="flex flex-col items-end gap-3 text-right">
                    <span className="inline-flex items-center gap-1 text-base font-semibold text-gray-900">
                        <ClockIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        {getFormattedDate(item.departure_date)}
                    </span>
                    {
                        containersCount > 0 && (
                            <button
                                type="button"
                                onClick={() => setActive(!isActive)}
                                aria-expanded={isActive}
                                aria-controls={collapsibleId}
                                className="inline-flex items-center gap-1 bg-indigo-50/80 px-3 py-1 text-xs font-semibold rounded-lg text-indigo-600 transition hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                            >
                                {t(isActive ? "dashboard.unconfirmed_shipping.hide_details" : "dashboard.unconfirmed_shipping.show_details")}
                                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isActive ? 'rotate-180' : ''}`} aria-hidden="true" />
                            </button>
                        )
                    }
                </div>
            </div>

            {
                isActive && containersCount > 0 && (
                    <div id={collapsibleId} className="mt-4 space-y-3 bg-indigo-50/40 p-3 rounded-lg">
                        {
                            item.containers.map((data) => {
                                const containerUrl = getWebPathParam([LOGISTICS, SHIPPING, item.id, CONTAINER, data.id, C_CONTAINER_GENERAL_INFORMATION]);
                                return (
                                    <a
                                        key={data.id}
                                        href={containerUrl}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            onPreview(containerUrl);
                                        }}
                                        className="flex items-center justify-between gap-3 bg-white/95 p-3 rounded-lg text-sm font-medium text-gray-700 shadow transition hover:-translate-y-0.5 hover:bg-white"
                                    >
                                        <div className="text-left">
                                            {renderLoadIdentifiers({ shippingTypeMeta, item, data, t })}

                                            {
                                                (data?.container_note ?? '').length > 0 && (
                                                <div className="text-xs text-gray-500">
                                                    {data.container_note}
                                                </div>
                                            )
                                        }
                                    </div>
                                    <div className="text-right text-xs text-gray-500">
                                        {data.load_date ? getCustomFormat(data.load_date, 'DD/MM/YYYY HH:mm') : "-"}
                                    </div>
                                </a>
                                )
                            })
                        }
                    </div>
                )
            }

        </article>
    )
}

const getShippingTypeMeta = (code, t) => {
    if (!code) {
        return null;
    }

    const shippingTypes = [
        { type: LAND, name: t("app.shippings.land_title"), containerLabel: t("app.container.truck"), addContainerLabel: t("app.container.add_truck"), addContainerTitle: t("app.container.new_truck"), editContainerTitle: t("app.container.edit_tuck_title"), icon: TruckIcon },
        { type: SEA, name: t("app.shippings.sea_title"), containerLabel: t("app.container.container"), addContainerLabel: t("app.container.add_contaienr"), addContainerTitle: t("app.container.new_contaienr"), editContainerTitle: t("app.container.edit_title"), icon: ShippingIcon },
        { type: AIR, name: t("app.shippings.air_title"), containerLabel: t("app.container.uld"), addContainerLabel: t("app.container.add_uld"), addContainerTitle: t("app.container.new_uld"), editContainerTitle: t("app.container.edit_uld_title"), icon: AirIcon },
    ];

    return shippingTypes.find((shippingType) => shippingType.type === code) ?? null;
};

const SummaryCard = ({ label, value, helper, icon, variant = "secondary" }) => {
    const baseClasses = "flex items-center justify-between px-5 py-4 ";
    const primaryClasses = "bg-gradient-to-r from-blue-1000 to-blue-1000 text-white shadow-indigo-500/30";
    const secondaryClasses = "border border-gray-100 bg-white text-gray-900";

    return (
        <div className={`${baseClasses} ${variant === "primary" ? primaryClasses : secondaryClasses}`}>
            <div>
                <p className={`text-xs font-semibold uppercase tracking-wide ${variant === "primary" ? "text-white/70" : "text-gray-500"}`}>
                    {label}
                </p>
                <p className="mt-1 text-3xl font-extrabold">
                    {value}
                </p>
                {
                    helper && (
                        <p className={`mt-1 text-xs ${variant === "primary" ? "text-white/70" : "text-gray-500"}`}>
                            {helper}
                        </p>
                    )
                }
            </div>
            <div className={`${variant === "primary" ? "text-white" : "text-blue-1000"} flex-shrink-0`}>
                {icon}
            </div>
        </div>
    );
};

const getShippingBadgeClasses = (code) => {
    switch (code) {
        case SEA:
            return "bg-blue-50 text-blue-700";
        case LAND:
            return "bg-amber-50 text-amber-700";
        case AIR:
            return "bg-sky-50 text-sky-700";
        default:
            return "bg-gray-50 text-gray-600";
    }
};

const getShippingTypeLabel = (shippingType) => {
    if (!shippingType) {
        return "";
    }

    if (shippingType?.name) {
        return shippingType.name;
    }

    return shippingType?.code?.toUpperCase() ?? "";
};

function renderLoadIdentifiers({ shippingTypeMeta, item, data, t }) {
    const typeCode = shippingTypeMeta?.type ?? item?.shipping_type?.code;
    const isSeaLoad = typeCode === SEA;
    const fallbackLabel = shippingTypeMeta?.containerLabel ?? t("app.container.container_not_defined");
    const loadType = isSeaLoad
        ? (data?.container_type?.name ?? fallbackLabel)
        : (data?.delivery_type?.name ?? fallbackLabel);
    const identifier = isSeaLoad
        ? (data?.container_number ?? null)
        : (data?.license_plate ?? null);

    return (
        <>
            {
                identifier && (
                    <div className="text-xs text-gray-700">
                        {identifier}
                    </div>
                )
            }
            <div className="text-sm font-semibold text-gray-900">
                {loadType}
            </div>
        </>
    );
}

const resolveFrameUrl = (url) => {
    if (!url) {
        return null;
    }

    if (url.startsWith("http")) {
        return url;
    }

    if (typeof window !== "undefined") {
        return `${window.location.origin}${url}`;
    }

    return url;
};

const FullscreenFrame = ({ isOpen, onClose, url }) => {
    const { t } = useTranslation();
    const frameUrl = url;

    if (!frameUrl) {
        return null;
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-[600]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Dialog.Overlay className="fixed inset-0 bg-gray-900/80 backdrop-blur" />
                </Transition.Child>

                <div className="flex min-h-screen items-stretch justify-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-6"
                        enterTo="opacity-100 translate-y-0"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-6"
                    >
                        <Dialog.Panel className="relative flex h-screen w-screen flex-col bg-white shadow-2xl">
                            <div className="flex items-center justify-between bg-blue-1000 px-6 py-4 text-white shadow">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
                                        {t("dashboard.unconfirmed_shipping.title")}
                                    </p>
                                    <p className="text-sm font-semibold">
                                        {t("app.shipping_tracking.view_title")}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="inline-flex items-center rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">{t("app.common.close")}</span>
                                        <XIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-hidden bg-gray-100">
                                <iframe
                                    src={frameUrl}
                                    title="Shipping preview"
                                    className="h-full w-full border-0 bg-white"
                                />
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

export default UnconfirmedShipping;
